import { FE_SESSION_ID_KEY } from 'constants/session';
import { isForcedDisconnectReason, useAuthStore } from 'lib/stores/authStore';
import { useRegistrationStore } from 'stores/zustand/registrationStore';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { removeLocalStorageItem } from 'utils/storage';
import { removeThemeCookie } from 'utils/theme-cookie';

/**
 * Auth-driven session teardown. Subscribes to authStore transitions set by the
 * transport layer and fires the cleanup that follows from them:
 *
 * - A forced disconnect (session_expired / maintenance / unavailability) clears
 *   the per-session storage artifacts here and hands off to `onForcedDisconnect`
 *   for the rest of teardown (appEffects registers that callback — cancelling
 *   the debounced notification-pref save and disposing the feature stores).
 *   'manual' logouts intentionally don't fire it: explicit sign-out runs its
 *   own teardown via UserStore.endSession(), so firing here would double up.
 * - A 401 response (unauthorizedResponseToken bump) ends the registration session,
 *   whether or not it comes with a full logout. It often comes without one: an
 *   applicant mid-signup (a magic-link verification, say) holds a registration user
 *   id while `authStore.userId` is still empty, so `handleGlobalError` has nothing to
 *   log out and only bumps this token. Clearing just the registration user id there
 *   would leave that applicant's persisted email and recovery data — and the store's
 *   session generation, so late callbacks could refill the flow — for the next person
 *   in this tab.
 *
 * Kept separate from sessionEffects.ts (the analytics fanout) because that one
 * only runs where SessionEffectsBootstrap is mounted, while this must cover
 * every AppEffectsBootstrap usage — including the embedded (charts) layout, which
 * deliberately loads no analytics.
 */
export function startSessionTeardownEffects(onForcedDisconnect: () => void): () => void {
    return useAuthStore.subscribe((state, prev) => {
        if (
            state.logoutReason !== prev.logoutReason &&
            state.logoutReason !== null &&
            isForcedDisconnectReason(state.logoutReason)
        ) {
            // These erasures go through the safe storage/cookie helpers, which
            // swallow the SecurityError that Web Storage and `document.cookie`
            // access raise in a sandboxed iframe or a browser configured to block
            // site data. This listener runs inside authStore's set(), so a throw
            // would propagate back into the logout that triggered it and strand
            // onForcedDisconnect() — the part that actually protects the next
            // session in this tab.
            removeLocalStorageItem(FE_SESSION_ID_KEY);
            removeLocalStorageItem('kalshi-color-palette');
            removeThemeCookie();
            onForcedDisconnect();
        }

        if (state.unauthorizedResponseToken !== prev.unauthorizedResponseToken) {
            // endSession(), not registrationSessionStore.userIdCleared(): that one is
            // only the session-storage user id, and it is the first thing this does.
            // Idempotent, so the logged-in 401 path (which also runs the forced-
            // disconnect teardown above) just repeats it harmlessly. A 401 always
            // means the session cannot continue — a wrong password is a 400 — so this
            // never discards a signup that could still be resumed.
            useRegistrationStore.getState().actions.endSession();
            logProviderFactory.cleanUser();
        }
    });
}
